import React, { JSX, useContext, useEffect, useMemo, useState } from "react";
import { MedicareNetwork } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { printNumberAsString } from "@app/shared/waffles";
import { Link } from "react-router-dom";
import { cloneDeep } from "lodash";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import BenchmarkingServiceAreaFilters from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingServiceAreaFilters";
import NetworkFlyoutCountiesTable from "@app/overviews/network-benchmarking-dashboard/filters/NetworkFlyoutCountiesTable";
import NetworkFlyoutPlansTable from "@app/overviews/network-benchmarking-dashboard/filters/NetworkFlyoutPlansTable";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";
import { useMedicareContractDetails } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/hooks/useMedicareContractDetails";
import { MedicareFiltersContext } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/MedicareFiltersContext";

interface MedicareNetworkFlyoutProps {
  contract: MedicareNetwork;
}

const MedicareNetworkFlyout = (props: MedicareNetworkFlyoutProps): JSX.Element => {
  const [isYourNetwork, setIsYourNetwork] = useState<boolean>(false);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const benchmarkingMedicareFiltersContext = useContext(MedicareFiltersContext);
  const userContext = useContext(UserContext);

  useEffect(() => {
    if (!!props.contract?.contractId) {
      const yourNetworks: MedicareNetwork[] = benchmarkingMedicareFiltersContext.parentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks")?.hContracts;
      setIsYourNetwork((yourNetworks.map(x => x.contractId).includes(props.contract?.contractId)));
    }
  }, [props.contract?.contractId]);

  const { contractDetails, isLoading: loading } = useMedicareContractDetails(
    props.contract?.contractId,
    props.contract?.versionId,
    benchmarkingFiltersContext.displayedPrimaryFilters
  );

  const contract = useMemo(() => {
    if (!contractDetails) return null;

    return {
      ...cloneDeep(props.contract),
      counties: contractDetails.counties,
      marketingName: contractDetails.marketingName,
      organizationName: contractDetails.organizationName,
      plans: contractDetails.plans,
    };
  }, [contractDetails, props.contract]);

  return (
    <React.Fragment>
      <BenchmarkingServiceAreaFilters />

      <div className="row">
        <div className="col-12">
          {loading ? (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          ) : (
            <AccordionContainer>
              <Accordion
                defaultOpen={true}
                title="Payer Details"
                testId="h-contract-flyout__payer-details-accordion"
              >
                <div className="row">
                  <div className="col-md-3">
                    <label className="demibold">
                      Marketing Name:
                    </label>
                  </div>
                  <div className="col-md-9">
                    {contract?.marketingName}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label className="demibold">
                      Organization Name:
                    </label>
                  </div>
                  <div className="col-md-9">
                    {contract?.organizationName}
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3">
                    <label className="demibold mb-0">
                      Parent Organization:
                    </label>
                  </div>
                  <div className="col-md-9">
                    {contract?.parentOrganization}
                  </div>
                </div>
              </Accordion>

              <Accordion
                title={`${!isYourNetwork ? "Active " : ""}Counties`}
                testId="h-contract-flyout__counties-accordion"
              >
                <NetworkFlyoutCountiesTable
                  contract={contract}
                  data={contract?.counties}
                  isYourNetwork={isYourNetwork}
                  loading={loading}
                  onApplyServiceAreaClick={benchmarkingMedicareFiltersContext.onApplyServiceAreaClick}
                />
              </Accordion>

              <Accordion
                title="Plans"
                testId="h-contract-flyout__plans-accordion"
              >
                <NetworkFlyoutPlansTable
                  contractId={contract?.contractId}
                  data={contract?.plans}
                />
              </Accordion>

              {(userContext.roles.includes(Roles.CLIENT_ADMIN) || isYourNetwork) && (
                <Accordion
                  title="Data Details"
                  testId="h-contract-flyout__data-details"
                >
                  <div className="row">
                    <div className="col-md-12">
                      <label className="label-m">
                        QES Data
                      </label>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="demibold">
                        QES Project Id:
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.qesProjectId ?
                        <Link to={`/projects/${props.contract.qesProjectId}`} target="_blank">{props.contract.qesProjectId}</Link> :
                        "---"
                      }
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="demibold">
                        QES Active County Count:
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.qesActiveCountyCount ? printNumberAsString(props.contract?.qesActiveCountyCount, "---") : "---"}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-3">
                      <label className="demibold">
                        CMS Active County Count:
                      </label>
                    </div>
                    <div className="col-md-9">
                      {!!props.contract?.cmsCountyCount ? printNumberAsString(props.contract?.cmsCountyCount, "---") : "---"}
                    </div>
                  </div>

                  {userContext.roles.includes(Roles.CLIENT_ADMIN) && (
                    <React.Fragment>
                      <div className="row">
                        <div className="col-md-12">
                          <label className="label-m">
                            Publicly Available Data
                          </label>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-3">
                          <label className="demibold">
                            Publicly Available Data:
                          </label>
                        </div>
                        <div className="col-md-9">
                          {props.contract?.isSupersetAvailable ? "Yes" : "No"}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-3">
                          <label className="demibold">
                            Facility Data Present:
                          </label>
                        </div>
                        <div className="col-md-9">
                          {props.contract?.isFacilityDataPresent ? "Yes" : "No"}
                        </div>
                      </div>
                    </React.Fragment>
                  )}
                </Accordion>
              )}
            </AccordionContainer>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default MedicareNetworkFlyout;
